import { useTranslation } from 'react-i18next';

const SERVICE_ICONS = [
  'ri-draft-line',
  'ri-code-s-slash-line',
  'ri-brain-line',
] as const;

type ServiceItem = {
  title: string;
  description: string;
  features: string[];
};

export default function Services() {
  const { t } = useTranslation();
  const list = t('services.list', { returnObjects: true }) as ServiceItem[];
  const services = list.map((item, index) => ({
    ...item,
    icon: SERVICE_ICONS[index] ?? 'ri-service-line',
  }));

  return (
    <section
      id="services"
      className="py-24 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden"
    >
      {/* 与「关于我们」同规则：斜向底色 + 双光晕，无方格网 */}
      <div
        className="pointer-events-none absolute top-1/4 right-1/4 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-1/4 left-1/4 h-80 w-80 rounded-full bg-teal-500/10 blur-3xl"
        aria-hidden
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm mb-6">
            <i className="ri-service-line text-cyan-400"></i>
            <span className="text-cyan-400 text-sm font-medium">{t('services.badge')}</span>
          </div>
          <h4 className="text-4xl lg:text-5xl font-black text-white mb-6">
            {t('services.title')}
          </h4>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            {t('services.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative p-8 rounded-2xl bg-gradient-to-br from-slate-900/50 to-slate-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 backdrop-blur-sm hover:shadow-xl hover:shadow-cyan-500/10"
            >
              {/* Glow Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/0 to-teal-500/0 group-hover:from-cyan-500/5 group-hover:to-teal-500/5 transition-all duration-300"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30 mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${service.icon} text-3xl text-cyan-400`}></i>
                </div>

                <h5 className="text-xl font-bold text-white mb-4">
                  {service.title}
                </h5>

                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <button className="mt-6 text-cyan-400 text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all cursor-pointer">
                  {t('services.cta')}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
